import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'flowbite-react';
import { Changes, GitlabOauth } from '../../services';
import type { GitlabOauthStatus } from '../../services/GitlabOauth';
import { formatMomentDate } from '../../locales/i18n';
import { Alert, Button, Confirm, DiffView, Icon, Markdown } from '..';
import { diffFileDomId } from '../DiffView/DiffView';

export interface ChangeMergeRequestPanelProps {
    changeId: string;
    mrLink?: string | null;
}

type MergeRequestUser = {
    name: string;
    username: string;
    avatarUrl?: string | null;
    webUrl?: string | null;
};

type MergeRequestPipeline = {
    id: number | null;
    status: string | null;
    webUrl: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
};

type MergeRequestBundle = {
    mergeRequest: {
        iid: number;
        title: string;
        description?: string;
        state: string;
        webUrl: string;
        sourceBranch: string;
        targetBranch: string;
        author?: MergeRequestUser | null;
        mergedBy?: MergeRequestUser | null;
        createdAt?: string | null;
        updatedAt?: string | null;
        mergedAt?: string | null;
        additions: number;
        deletions: number;
        userNotesCount: number;
        mergeStatus?: string | null;
        detailedMergeStatus?: string | null;
        hasConflicts?: boolean;
        rebaseInProgress?: boolean;
        mergeError?: string | null;
        pipeline?: MergeRequestPipeline | null;
    };
    commits: Array<{
        id: string;
        shortId: string;
        title: string;
        authorName: string;
        authoredDate: string | null;
        webUrl: string | null;
    }>;
    files: Array<{
        oldPath: string | null;
        newPath: string | null;
        newFile: boolean;
        deletedFile: boolean;
        renamedFile: boolean;
    }>;
    diff: string;
    diffTruncated?: boolean;
    gitlabOauth?: GitlabOauthStatus;
    mergeRequests?: {
        squash: boolean;
        squash_commit_message: 'first_commit' | 'gitlab_default';
        remove_source_branch: boolean;
    };
    approval?: {
        userHasApproved: boolean;
        userCanApprove: boolean;
        approved: boolean;
        approvedByCount: number;
    } | null;
};

function formatMrDate(value?: string | null): string {
    if (!value)
        return '';
    return `${formatMomentDate(value, 'll')} ${formatMomentDate(value, 'LT')}`;
}

function displayUserLabel(user?: MergeRequestUser | null): string {
    if (!user)
        return '';
    const name = user.name || user.username || '';
    if (user.username && user.name && user.username !== user.name)
        return `${user.name} (@${user.username})`;
    return name;
}

type PipelineBadgeColor =
    | 'success'
    | 'failure'
    | 'warning'
    | 'info'
    | 'purple'
    | 'gray';

function pipelineBadgeColor(status?: string | null): PipelineBadgeColor {
    switch (status) {
        case 'success':
            return 'success';
        case 'failed':
            return 'failure';
        case 'running':
            return 'info';
        case 'pending':
        case 'created':
        case 'waiting_for_resource':
        case 'preparing':
            return 'warning';
        case 'manual':
        case 'scheduled':
            return 'purple';
        case 'canceled':
        case 'skipped':
        default:
            return 'gray';
    }
}

function pipelineChipClass(status?: string | null): string {
    switch (status) {
        case 'success':
            return 'bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-300';
        case 'failed':
            return 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300';
        case 'running':
            return 'bg-cyan-50 text-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300';
        case 'pending':
        case 'created':
        case 'waiting_for_resource':
        case 'preparing':
            return 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300';
        case 'manual':
        case 'scheduled':
            return 'bg-purple-50 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300';
        case 'canceled':
        case 'skipped':
        default:
            return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200';
    }
}

function ChangeMergeRequestPanel(props: ChangeMergeRequestPanelProps) {
    const { changeId, mrLink } = props;
    const { t } = useTranslation();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [bundle, setBundle] = useState<MergeRequestBundle | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [actionSuccess, setActionSuccess] = useState<string | null>(null);
    const [busyAction, setBusyAction] = useState<'approve' | 'merge' | 'comment' | 'rebase' | null>(null);
    const [needRebaseHint, setNeedRebaseHint] = useState(false);
    const [comment, setComment] = useState('');
    const [showCommentComposer, setShowCommentComposer] = useState(false);
    const [oauthConnecting, setOauthConnecting] = useState(false);
    const [oauthDisconnecting, setOauthDisconnecting] = useState(false);
    const [showApproveConfirm, setShowApproveConfirm] = useState(false);
    const [showMergeConfirm, setShowMergeConfirm] = useState(false);

    useEffect(() => {
        const el = rootRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            setActive(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting))
                    setActive(true);
            },
            { threshold: 0.01 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const reload = useCallback(() => {
        setLoading(true);
        setError(null);
        return Changes.getMergeRequest(changeId)
            .then((data) => {
                const next = data as MergeRequestBundle;
                setBundle(next);
                if (next.mergeRequest?.detailedMergeStatus !== 'need_rebase')
                    setNeedRebaseHint(false);
            })
            .catch((err: Error) => {
                setError(err?.message || t('changes.merge_request.load_error') || '');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [changeId, t]);

    useEffect(() => {
        if (!active || bundle)
            return;

        let cancelled = false;
        setLoading(true);
        setError(null);

        Changes.getMergeRequest(changeId)
            .then((data) => {
                if (!cancelled)
                    setBundle(data as MergeRequestBundle);
            })
            .catch((err: Error) => {
                if (!cancelled)
                    setError(err?.message || t('changes.merge_request.load_error') || '');
            })
            .finally(() => {
                if (!cancelled)
                    setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [active, bundle, changeId, t]);

    const connectGitlab = async () => {
        setOauthConnecting(true);
        setActionError(null);
        try {
            const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            const { url } = await GitlabOauth.start(returnTo);
            window.location.href = url;
        } catch (err) {
            setActionError((err as Error)?.message || t('changes.merge_request.oauth_start_error') || '');
            setOauthConnecting(false);
        }
    };

    const disconnectGitlab = async () => {
        setOauthDisconnecting(true);
        setActionError(null);
        setActionSuccess(null);
        try {
            await GitlabOauth.disconnect();
            setBundle((prev) => (
                prev
                    ? {
                        ...prev,
                        gitlabOauth: {
                            ...(prev.gitlabOauth || { configured: true, connected: false }),
                            connected: false,
                            username: null,
                        },
                        approval: null,
                    }
                    : prev
            ));
        } catch (err) {
            setActionError(
                (err as Error)?.message || t('changes.merge_request.oauth_disconnect_error') || ''
            );
        } finally {
            setOauthDisconnecting(false);
        }
    };

    const mapOauthError = (err: Error & { code?: string }, fallbackKey: string) => {
        if (err.code === 'gitlab_oauth_required' || err.code === 'gitlab_oauth_expired')
            return t('changes.merge_request.oauth_required') || err.message;
        if (err.code === 'gitlab_mr_need_rebase')
            return t('changes.merge_request.need_rebase_error') || err.message;
        return err.message || t(fallbackKey) || '';
    };

    const runAction = async (
        action: 'approve' | 'merge' | 'comment' | 'rebase',
        runner: () => Promise<unknown>,
        successKey: string,
        errorKey: string,
        options: { clearComment?: boolean; reload?: boolean } = {}
    ) => {
        setBusyAction(action);
        setActionError(null);
        setActionSuccess(null);
        try {
            await runner();
            setActionSuccess(t(successKey) || '');
            if (options.clearComment)
                setComment('');
            if (options.reload !== false)
                await reload();
            return true;
        } catch (err) {
            const typed = err as Error & { code?: string };
            if (typed.code === 'gitlab_mr_need_rebase')
                setNeedRebaseHint(true);
            setActionError(mapOauthError(typed, errorKey));
            return false;
        } finally {
            setBusyAction(null);
        }
    };

    const confirmApprove = async () => {
        setShowApproveConfirm(false);
        await runAction(
            'approve',
            () => Changes.approveMergeRequest(changeId),
            'changes.merge_request.approve_success',
            'changes.merge_request.approve_error'
        );
    };

    const confirmMerge = async () => {
        setShowMergeConfirm(false);
        setNeedRebaseHint(false);
        await runAction(
            'merge',
            () => Changes.mergeMergeRequest(changeId),
            'changes.merge_request.merge_success',
            'changes.merge_request.merge_error'
        );
    };

    const rebase = async (skipCi: boolean) => {
        setNeedRebaseHint(false);
        await runAction(
            'rebase',
            () => Changes.rebaseMergeRequest(changeId, { skip_ci: skipCi }),
            skipCi
                ? 'changes.merge_request.rebase_skip_ci_success'
                : 'changes.merge_request.rebase_success',
            'changes.merge_request.rebase_error'
        );
    };

    const cancelCommentComposer = () => {
        setShowCommentComposer(false);
        setComment('');
        setActionError(null);
    };

    const publishComment = async () => {
        const body = comment.trim();
        if (!body) {
            setActionError(t('changes.merge_request.comment_required') || '');
            return;
        }
        const ok = await runAction(
            'comment',
            () => Changes.commentMergeRequest(changeId, body),
            'changes.merge_request.comment_success',
            'changes.merge_request.comment_error',
            { clearComment: true, reload: false }
        );
        if (ok)
            setShowCommentComposer(false);
    };

    const waiting = !active || loading || (!bundle && !error);

    if (waiting) {
        return (
            <div ref={rootRef} className="flex justify-center py-8 min-h-[3rem]">
                {active ? <Icon type="loading" color="gray-500" size={8} /> : null}
            </div>
        );
    }

    if (error) {
        return (
            <div ref={rootRef} className="space-y-3">
                <Alert title={error} icon="warning" color="warning" />
                {mrLink && (
                    <Button
                        title={t('changes.merge_request.open_gitlab') || ''}
                        icon="gitlab"
                        color="secondary"
                        small
                        to={mrLink}
                        target="_blank"
                    />
                )}
            </div>
        );
    }

    if (!bundle?.mergeRequest) {
        return (
            <div ref={rootRef}>
                <Alert
                    title={t('changes.merge_request.empty') || ''}
                    icon="warning"
                    color="warning"
                />
            </div>
        );
    }

    const { mergeRequest, commits, files, diff, diffTruncated, gitlabOauth, mergeRequests, approval } = bundle;
    const openUrl = mergeRequest.webUrl || mrLink || '';
    const canMerge = mergeRequest.state === 'opened';
    const oauthConfigured = !!gitlabOauth?.configured;
    const oauthConnected = !!gitlabOauth?.connected;
    const squashEnabled = mergeRequests?.squash !== false;
    const userHasApproved = !!approval?.userHasApproved;
    const needsRebase = needRebaseHint
        || mergeRequest.detailedMergeStatus === 'need_rebase';
    const rebaseInProgress = !!mergeRequest.rebaseInProgress;
    const hasConflicts = !!mergeRequest.hasConflicts;
    const author = mergeRequest.author || null;
    const authorLabel = displayUserLabel(author) || '—';
    const mergedBy = mergeRequest.mergedBy || null;
    const mergedByLabel = displayUserLabel(mergedBy);
    const pipeline = mergeRequest.pipeline || null;
    const pipelineStatus = pipeline?.status || null;
    const pipelineStatusLabel = pipelineStatus
        ? (t(`changes.merge_request.pipeline_status.${pipelineStatus}`, {
            defaultValue: pipelineStatus,
        }) || pipelineStatus)
        : '';
    const createdAtLabel = formatMrDate(mergeRequest.createdAt);
    const rawUpdatedAtLabel = formatMrDate(mergeRequest.updatedAt);
    const updatedAtLabel = mergeRequest.state !== 'merged'
        && rawUpdatedAtLabel
        && rawUpdatedAtLabel !== createdAtLabel
        ? rawUpdatedAtLabel
        : '';
    const mergedAtLabel = formatMrDate(mergeRequest.mergedAt);
    const mergeLabel = t(
        squashEnabled
            ? 'changes.merge_request.merge_squash'
            : 'changes.merge_request.merge'
    ) || '';
    const mergeConfirmTitle = t(
        squashEnabled
            ? 'changes.merge_request.merge_squash_confirm'
            : 'changes.merge_request.merge_confirm'
    ) || '';

    return (
        <div ref={rootRef} className="flex flex-col gap-4 pr-4 text-sm text-gray-900 dark:text-gray-100">
            <div className="flex flex-wrap items-center gap-2 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 rounded">
                <Badge color={mergeRequest.state === 'merged' ? 'success' : mergeRequest.state === 'opened' ? 'info' : 'gray'}>
                    {mergeRequest.state}
                </Badge>
                <span className="font-semibold">!{mergeRequest.iid}</span>
                <span className="min-w-0 flex-1 truncate">{mergeRequest.title}</span>
                <span className="font-mono text-xs text-green-700 dark:text-green-400">+{mergeRequest.additions}</span>
                <span className="font-mono text-xs text-red-700 dark:text-red-400">−{mergeRequest.deletions}</span>
                {openUrl && (
                    <Button
                        title=""
                        icon="gitlab"
                        iconSize={4}
                        color="secondary"
                        small
                        to={openUrl}
                        target="_blank"
                    />
                )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 text-xs text-gray-600 dark:text-gray-300">
                <div className="flex min-w-0 flex-col gap-2">
                    {author && (
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-700 dark:text-gray-200">
                                {t('changes.merge_request.author')}
                            </span>
                            {author.avatarUrl ? (
                                <img
                                    src={author.avatarUrl}
                                    alt=""
                                    className="h-5 w-5 rounded-full object-cover"
                                />
                            ) : null}
                            {author.webUrl ? (
                                <a
                                    href={author.webUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-indigo-600 dark:text-indigo-300 hover:underline"
                                >
                                    {authorLabel}
                                </a>
                            ) : (
                                <span>{authorLabel}</span>
                            )}
                        </div>
                    )}
                    <div className="text-gray-500 dark:text-gray-400">
                        <code>{mergeRequest.sourceBranch}</code>
                        <Icon type="right" size={3} color="current" className="inline mx-1" />
                        <code>{mergeRequest.targetBranch}</code>
                    </div>
                </div>

                <div className="flex flex-col gap-1.5 items-start sm:items-end shrink-0">
                    {createdAtLabel && (
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1 text-gray-700 dark:text-gray-200">
                            <span className="font-medium text-gray-500 dark:text-gray-400">
                                {t('changes.merge_request.created_at')}
                            </span>
                            <span>{createdAtLabel}</span>
                            <Icon type="date" size={3} color="current" />
                        </span>
                    )}
                    {updatedAtLabel && (
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1 text-gray-700 dark:text-gray-200">
                            <span className="font-medium text-gray-500 dark:text-gray-400">
                                {t('changes.merge_request.updated_at')}
                            </span>
                            <span>{updatedAtLabel}</span>
                            <Icon type="sync" size={3} color="current" />
                        </span>
                    )}
                    {mergedAtLabel && (
                        <span className="inline-flex flex-wrap items-center gap-1.5 rounded-md bg-green-50 dark:bg-green-950/40 px-2 py-1 text-green-800 dark:text-green-300 sm:justify-end">
                            {mergedByLabel ? (
                                <span>
                                    {t('changes.merge_request.merged_at_by', {
                                        date: mergedAtLabel,
                                        name: mergedByLabel,
                                    })}
                                </span>
                            ) : (
                                <>
                                    <span className="font-medium text-green-700/80 dark:text-green-400/80">
                                        {t('changes.merge_request.merged_at')}
                                    </span>
                                    <span>{mergedAtLabel}</span>
                                </>
                            )}
                            <Icon type="merge" size={3} color="current" />
                        </span>
                    )}
                    <span className={`inline-flex flex-wrap items-center gap-1.5 rounded-md px-2 py-1 sm:justify-end ${pipelineChipClass(pipelineStatus)}`}>
                        <span className="font-medium opacity-70">
                            {t('changes.merge_request.pipeline')}
                        </span>
                        {pipelineStatus ? (
                            <>
                                <Badge color={pipelineBadgeColor(pipelineStatus)}>
                                    {pipelineStatusLabel}
                                </Badge>
                                {pipeline?.webUrl ? (
                                    <a
                                        href={pipeline.webUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-medium underline-offset-2 hover:underline"
                                    >
                                        #{pipeline.id || '—'}
                                    </a>
                                ) : (
                                    <span>#{pipeline?.id || '—'}</span>
                                )}
                            </>
                        ) : (
                            <span className="opacity-70">
                                {t('changes.merge_request.pipeline_none')}
                            </span>
                        )}
                        <Icon type="build" size={3} color="current" />
                    </span>
                </div>
            </div>

            <section className="border border-gray-100 dark:border-gray-700 rounded p-3">
                <h3 className="font-semibold mb-2">
                    {t('changes.merge_request.description')}
                </h3>
                {mergeRequest.description?.trim() ? (
                    <div className="max-h-80 overflow-y-auto text-sm">
                        <Markdown content={mergeRequest.description} />
                    </div>
                ) : (
                    <p className="text-gray-500 dark:text-gray-400">
                        {t('changes.merge_request.no_description')}
                    </p>
                )}
            </section>

            {canMerge && (
                <section className="flex flex-col gap-3 border border-gray-100 dark:border-gray-700 rounded p-3">
                    <div className="flex flex-wrap items-center gap-2">
                        {oauthConfigured && !oauthConnected && (
                            <Button
                                title={t('changes.merge_request.connect_gitlab') || ''}
                                icon="gitlab"
                                color="secondary"
                                small
                                disabled={oauthConnecting}
                                onClick={connectGitlab}
                            />
                        )}
                        {oauthConfigured && oauthConnected && (
                            <span className="flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                                <span>
                                    {t('changes.merge_request.connected_as', {
                                        username: gitlabOauth?.username || '—',
                                    })}
                                </span>
                                <button
                                    type="button"
                                    disabled={oauthDisconnecting || !!busyAction}
                                    onClick={disconnectGitlab}
                                    className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 hover:underline disabled:opacity-50 disabled:no-underline"
                                >
                                    <Icon type="logout" size={3} color="current" />
                                    {t('changes.merge_request.disconnect_gitlab')}
                                </button>
                            </span>
                        )}
                        {!oauthConfigured && (
                            <Alert
                                title={t('changes.merge_request.oauth_not_configured') || ''}
                                icon="warning"
                                color="warning"
                            />
                        )}
                    </div>

                    {oauthConfigured && oauthConnected && (
                        <>
                            {(needsRebase || rebaseInProgress || hasConflicts) && (
                                <Alert
                                    title={
                                        rebaseInProgress
                                            ? (t('changes.merge_request.rebase_in_progress') || '')
                                            : hasConflicts
                                                ? (t('changes.merge_request.has_conflicts') || '')
                                                : (t('changes.merge_request.need_rebase') || '')
                                    }
                                    icon="warning"
                                    color="warning"
                                />
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                                {!showCommentComposer && (
                                    <Button
                                        title={t('changes.merge_request.comment_submit') || ''}
                                        icon="comment"
                                        color="secondary"
                                        small
                                        disabled={!!busyAction}
                                        onClick={() => {
                                            setActionError(null);
                                            setShowCommentComposer(true);
                                        }}
                                    />
                                )}
                                <Button
                                    title={
                                        userHasApproved
                                            ? (t('changes.merge_request.approved') || '')
                                            : (t('changes.merge_request.approve') || '')
                                    }
                                    icon="thumbs-up"
                                    color={userHasApproved ? 'success' : 'primary'}
                                    small
                                    disabled={!!busyAction || userHasApproved}
                                    loading={busyAction === 'approve'}
                                    onClick={() => setShowApproveConfirm(true)}
                                />
                                {(needsRebase || rebaseInProgress) && (
                                    <>
                                        <Button
                                            title={t('changes.merge_request.rebase') || ''}
                                            icon="sync"
                                            color="warning"
                                            small
                                            disabled={!!busyAction || rebaseInProgress || hasConflicts}
                                            loading={busyAction === 'rebase' || rebaseInProgress}
                                            onClick={() => rebase(false)}
                                        />
                                        <Button
                                            title={t('changes.merge_request.rebase_skip_ci') || ''}
                                            icon="sync"
                                            color="secondary"
                                            small
                                            disabled={!!busyAction || rebaseInProgress || hasConflicts}
                                            loading={busyAction === 'rebase' || rebaseInProgress}
                                            onClick={() => rebase(true)}
                                        />
                                    </>
                                )}
                                <Button
                                    title={mergeLabel}
                                    icon="merge"
                                    color="success"
                                    small
                                    disabled={!!busyAction || needsRebase || rebaseInProgress || hasConflicts}
                                    loading={busyAction === 'merge'}
                                    onClick={() => setShowMergeConfirm(true)}
                                />
                            </div>
                            {showCommentComposer && (
                                <div className="flex flex-col gap-2">
                                    <label
                                        htmlFor={`mr-comment-${changeId}`}
                                        className="block text-xs font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        {t('changes.merge_request.comment_label')}
                                    </label>
                                    <textarea
                                        id={`mr-comment-${changeId}`}
                                        value={comment}
                                        onChange={(event) => setComment(event.target.value)}
                                        rows={3}
                                        autoFocus
                                        disabled={!!busyAction}
                                        placeholder={t('changes.merge_request.comment_placeholder') || ''}
                                        className="block w-full rounded-md border-0 py-1.5 text-sm text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-gray-900"
                                    />
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                            title={t('changes.merge_request.comment_publish') || t('changes.merge_request.comment_submit') || ''}
                                            icon="comment"
                                            color="primary"
                                            small
                                            disabled={!!busyAction || !comment.trim()}
                                            loading={busyAction === 'comment'}
                                            onClick={publishComment}
                                        />
                                        <Button
                                            title={t('common.cancel') || ''}
                                            color="secondary"
                                            small
                                            disabled={!!busyAction}
                                            onClick={cancelCommentComposer}
                                        />
                                    </div>
                                </div>
                            )}
                            <Confirm
                                visible={showApproveConfirm}
                                iconType="thumbs-up"
                                title={t('changes.merge_request.approve_confirm') || ''}
                                confirmText={t('changes.merge_request.approve') || ''}
                                cancelText={t('common.cancel') || ''}
                                confirmColor="purple"
                                onConfirm={confirmApprove}
                                onCancel={() => setShowApproveConfirm(false)}
                            />
                            <Confirm
                                visible={showMergeConfirm}
                                iconType="merge"
                                title={mergeConfirmTitle}
                                confirmText={mergeLabel}
                                cancelText={t('common.cancel') || ''}
                                confirmColor="success"
                                onConfirm={confirmMerge}
                                onCancel={() => setShowMergeConfirm(false)}
                            />
                        </>
                    )}

                    {actionError && (
                        <Alert title={actionError} icon="warning" color="warning" />
                    )}
                    {actionSuccess && (
                        <Alert title={actionSuccess} icon="check" color="success" />
                    )}
                </section>
            )}

            <section>
                <h3 className="font-semibold mb-2">
                    {t('changes.merge_request.files')} ({files.length})
                </h3>
                <ul className="max-h-40 overflow-y-auto border border-gray-100 dark:border-gray-700 rounded divide-y divide-gray-100 dark:divide-gray-700">
                    {files.map((file, index) => {
                        const path = file.newPath || file.oldPath || '—';
                        const canJump = path !== '—' && !diffTruncated && !!diff?.length;
                        return (
                            <li key={`${path}-${index}`} className="px-2 py-1 font-mono text-xs truncate">
                                {file.newFile && <span className="text-green-600 mr-1">A</span>}
                                {file.deletedFile && <span className="text-red-600 mr-1">D</span>}
                                {file.renamedFile && <span className="text-amber-600 mr-1">R</span>}
                                {canJump ? (
                                    <a
                                        href={`#${diffFileDomId(path)}`}
                                        className="text-indigo-600 dark:text-indigo-300 hover:underline"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            const target = document.getElementById(diffFileDomId(path));
                                            target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }}
                                    >
                                        {path}
                                    </a>
                                ) : (
                                    path
                                )}
                            </li>
                        );
                    })}
                    {!files.length && (
                        <li className="px-2 py-2 text-gray-500">{t('changes.merge_request.no_files')}</li>
                    )}
                </ul>
            </section>

            <section>
                <h3 className="font-semibold mb-2">
                    {t('changes.merge_request.commits')} ({commits.length})
                </h3>
                <ul className="max-h-48 overflow-y-auto border border-gray-100 dark:border-gray-700 rounded divide-y divide-gray-100 dark:divide-gray-700">
                    {commits.map((commit) => {
                        const commitDate = formatMrDate(commit.authoredDate);
                        return (
                            <li key={commit.id} className="px-2 py-1.5">
                                <div className="flex items-baseline gap-2">
                                    <code className="text-xs text-indigo-600 dark:text-indigo-300 shrink-0">
                                        {commit.shortId}
                                    </code>
                                    <span className="truncate">{commit.title}</span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {commit.authorName}
                                    {commitDate ? ` · ${commitDate}` : ''}
                                </div>
                            </li>
                        );
                    })}
                    {!commits.length && (
                        <li className="px-2 py-2 text-gray-500">{t('changes.merge_request.no_commits')}</li>
                    )}
                </ul>
            </section>

            <section>
                <h3 className="font-semibold mb-2">{t('changes.merge_request.diff')}</h3>
                {diffTruncated && (
                    <div className="mb-2">
                        <Alert
                            title={t('changes.merge_request.diff_truncated') || ''}
                            icon="warning"
                            color="warning"
                        />
                    </div>
                )}
                {!diffTruncated && diff?.length > 0 && (
                    <div className="overflow-x-auto">
                        <DiffView diff={diff} />
                    </div>
                )}
                {!diffTruncated && !(diff?.length > 0) && (
                    <p className="text-gray-500">{t('changes.merge_request.no_diff')}</p>
                )}
            </section>
        </div>
    );
}

export default ChangeMergeRequestPanel;
